import { isObject } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'
import tabsHelper from '../tabs-helper'

export default {
  name: 'basicInfoConfig',

  mixins: [
    tabsHelper
  ],

  data () {
    return {
      internalDataVisible: false,
      // Set from this tab's GET response: whether the record has anything in it
      // at all. Only a fetch can answer that - the form object has its keys well
      // before it has its data.
      roadInformationIsEmpty: false,
      // Whether this event was raised from a traffic regulation request, as the
      // GET response reports it. The request owns the schedule, so the whole top
      // block is read-only for such an event - see `roadInformationFromRequest`.
      roadInformationHasRequest: false,
      // Per-record edit permission from this tab's GET response
      // `meta.can_edit`. Absence => editable, so tabs whose backend doesn't
      // report it keep working as before.
      roadInformationCanEdit: true
    }
  },

  computed: {
    creatingRoadInformation () {
      return this.$route.query.category === 'request_new'
    },

    // Module-level permission AND the per-record one the backend reports as
    // `meta.can_edit` - either one saying no puts the tab in read-only mode.
    //
    // Neither applies to a record being created: there is no record yet to report a
    // `can_edit` for, and getting here at all took the new button, which is offered
    // on the listing only to whoever may create one. Asking again would only mean the
    // save and cancel buttons turning up a moment after the form they belong to.
    canEditRoadInformation () {
      if (this.creatingRoadInformation) {
        return true
      }

      return !!this.permissions?.canUpdate && this.roadInformationCanEdit
    },

    /**
     * The schedule belongs to the request, not to this screen: on the request's own
     * details it is always so, and on the road information screen whenever the event
     * came from one. The listing answers that in the URL it navigates with, since the
     * record the screen fetches does not report the link.
     */
    roadInformationFromRequest () {
      return !!this.fromRequest ||
        this.roadInformationHasRequest ||
        !!this.$route.query.from_request
    },

    roadInformation () {
      return {
        title: this.$t('road-maintenance.road_information'),
        key: 'road-information',
        visible: !this.creatingRoadInformation || !!this.$route.meta.show,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: () => this.getRoadInformationRoute(),
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: () => this.getRoadInformationRoute(),
            method: 'get'
          }
        },
        editUpdate: (event) => this.roadInformationEditUpdated(event),
        dataFetched: (event) => this.onRoadInfoDataFetched(event),
        afterCancel: (event) => this.onRoadInfoDataFetched(event),
        afterSave: (event) => this.onRoadInformationSaved(event),
        modifyDataBeforeSend: (event) => this.roadInfoModifyDataBeforeSend(event),
        // The closure type moved onto the street (one CIFS incident is one
        // street), so the event no longer carries `type_id`/`subtype_id`.
        hiddenFormKeys: [
          'streets',
          'start_time',
          'end_time',
          'status',
          'publish_to_public'
        ],
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.$t('road-maintenance.road_information'),
            showEditButtons: this.canEditRoadInformation,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            // Additional buttons are hidden while a tab is being edited or created
            // unless this says otherwise, and the publish switch is wanted in all
            // three states - it is the one control here that is not a button.
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                component: this.publishToPublicSwitchComponent
              }
            ],
            onCancelTabSave: (event) => this.onCancelRoadInformationCreate(event)
          },
          {
            component: this.drawRoadRegulationComponent,
            disabled: !this.canEditRoadInformation,
            // On the request screen the event's schedule and status come from the
            // request itself and are never edited here - see `showScheduleBlock`
            // in DrawRoadRegulation.
            readOnlySchedule: this.roadInformationFromRequest,
            // A closure raised from a request is only ever one of the types a
            // request can ask for, so the dropdown is asked for that subset.
            trafficRegulationTypesOnly: !!this.fromRequest,
            // A record with nothing in it opens on the form: there is no
            // information to present and nothing for an Edit button to reveal.
            startInEditMode: this.roadInformationIsEmpty,
            type: 'dynamic-columns',
            dataKey: 'streets'
          }
        ]
      }
    }
  },

  methods: {
    getRoadInformationRoute () {
      const { id } = this.$route.params || {}

      if (this.fromRequest) {
        return id
          ? `traffic-regulation/${id}/road-information`
          : 'traffic-regulation/road-information'
      }

      return id
        ? `traffic-activity-feed/${id}`
        : 'traffic-activity-feed'
    },

    roadInformationEditUpdated (event) {
      const { edit, createMode } = event || {}
      this.edit = !!edit

      this.internalDataVisible = !this.edit && !createMode
    },

    /**
     * A record that has just been created has nowhere to stay: the screen was opened
     * without an id, so it would sit there as a create form over a record that already
     * exists. Cancelling out of a create goes back to the listing too - see
     * `onCancelRoadInformationCreate` - and so does saving one.
     */
    onRoadInformationSaved (event) {
      this.onRoadInfoDataFetched(event)

      if (!this.creatingRoadInformation) {
        return
      }

      this.$router.push({
        name: 'Aggregator_road_information'
      }).then()
    },

    onCancelRoadInformationCreate (event) {
      const { cancelTabSave } = event || {}
      if (this.creatingRoadInformation) {
        this.$router.push({
          name: 'Aggregator_road_information'
        }).then()
      }
      else if (typeof cancelTabSave === 'function') {
        cancelTabSave()
      }
    },

    onRoadInfoDataFetched (event) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      // Only an explicit `false` locks the tab - `afterCancel`/`afterSave` reuse
      // this handler and pass no `meta`, which must not flip the flag.
      if (apiData?.meta && Object.prototype.hasOwnProperty.call(apiData.meta, 'can_edit')) {
        this.roadInformationCanEdit = apiData.meta.can_edit !== false
      }

      const data = apiData?.data || formDataForSend || {}

      this.roadInformationIsEmpty = !this.creatingRoadInformation && !data?.streets?.length

      // Read from the fetched record rather than from the form: the form only
      // carries the keys this tab sends back.
      if (apiData?.data) {
        this.roadInformationHasRequest = !!(apiData.data.traffic_regulation?.id || apiData.data.traffic_regulation_id)
      }

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          // Modify dates to be in format: FORMATS.datetimeShort
          // An empty date stays empty - the form must not fill itself in with the
          // current moment.
          start_time: data?.start_time ? dateService.formatCustom(data.start_time, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          end_time: data?.end_time ? dateService.formatCustom(data.end_time, FORMATS.isoDatetime, FORMATS.datetimeShort) : null
        })
      }
    },

    roadInfoModifyDataBeforeSend (event) {
      const { copiedData } = event || {}
      const { id: trafficRegulationId } = this.$route?.params || {}
      if (!copiedData) {
        return
      }

      // Modify dates
      copiedData.start_time = copiedData.start_time
        ? dateService.formatCustom(copiedData.start_time, FORMATS.datetimeShort, FORMATS.isoDatetime)
        : null
      copiedData.end_time = copiedData.end_time
        ? dateService.formatCustom(copiedData.end_time, FORMATS.datetimeShort, FORMATS.isoDatetime)
        : null

      // take an ids from objects (autocompletes)
      copiedData.status = copiedData.status?.id || copiedData.status

      // The API reads the maps flag as a number, not a boolean.
      copiedData.publish_to_public = copiedData.publish_to_public ? 1 : 0

      // The type and the subtype used to be one choice for the whole event; they
      // are now a single per-street field holding the leaf type's id, which is
      // what CIFS calls the subtype.
      delete copiedData.type_id
      delete copiedData.subtype_id

      // Clean up streets
      if (Array.isArray(copiedData.streets)) {
        copiedData.streets = copiedData.streets.map(street => {
          const normalized = {
            ...street,
            type_id: street.type_id?.id || street.type_id,
            direction_id: street.direction_id?.id || street.direction_id
          }

          if (normalized.new) {
            const { id, new: _omitNew, ...rest } = normalized // remove id and new flag
            return rest
          }

          return normalized
        })
      }

      // send traffic regulation id from then route params
      copiedData.traffic_regulation_id = trafficRegulationId

      return copiedData
    }
  }
}
